import { useState, useEffect } from 'react';

interface User {
  username: string;
  role: string;
  permissions: string[];
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    loading: true
  });

  // Verifica autenticazione al caricamento
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const userStr = localStorage.getItem('auth_user');

      if (!token || !userStr) {
        setAuthState(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        // Verifica se il token è ancora valido
        const response = await fetch('/api/auth/verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (data.success) {
          const user = JSON.parse(userStr);
          setAuthState({
            isAuthenticated: true,
            user,
            token,
            loading: false
          });
        } else {
          // Token non valido, rimuovi dai localStorage
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          setAuthState({
            isAuthenticated: false,
            user: null,
            token: null,
            loading: false
          });
        }
      } catch (error) {
        console.error('Auth verification error:', error);
        // In caso di errore, rimuovi dai localStorage
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        setAuthState({
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false
        });
      }
    };

    checkAuth();
  }, []);

  const login = (token: string, user: User) => {
    setAuthState({
      isAuthenticated: true,
      user,
      token,
      loading: false
    });
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false
      });
      // Ogni componente ha la propria istanza di useAuth (non è un Context): aggiornare solo
      // questo stato non faceva tornare l'app al login. Ricaricando, l'app riparte senza token.
      window.location.assign('/');
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!authState.user) return false;
    
    // Admin ha tutti i permessi
    if (authState.user.permissions.includes('*')) return true;
    
    return authState.user.permissions.includes(permission);
  };

  const isAdmin = (): boolean => {
    return authState.user?.role === 'admin' || false;
  };

  return {
    ...authState,
    login,
    logout,
    hasPermission,
    isAdmin
  };
};